import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { API_BASE_URL } from '../../config';
import {
  getKnowledgeDocumentInspectorChunkText,
  getKnowledgeDocumentInspectorHeader,
  getKnowledgeDocumentInspectorProfile,
  listKnowledgeDocumentInspectorChunks,
  listKnowledgeDocumentInspectorDocuments,
} from './api';
import type {
  KnowledgeDocumentInspectorChunk,
  KnowledgeDocumentInspectorListItem,
  KnowledgePaginatedResponse,
} from './types';

const EMPTY_PAGE: KnowledgePaginatedResponse<KnowledgeDocumentInspectorListItem> = {
  page: 1,
  limit: 20,
  total: 0,
  pages: 0,
  items: [],
};

function getErrorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Document Inspector kunde inte läsa data.';
}

function safeSourceUrl(value?: string): string | null {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/')) return `${API_BASE_URL}${value}`;
  return null;
}

const DocumentTextInspector: React.FC = () => {
  const [documents, setDocuments] = React.useState(EMPTY_PAGE);
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState('');
  const [sourceKind, setSourceKind] = React.useState('');
  const [extractionStatus, setExtractionStatus] = React.useState('');
  const [extractionQuality, setExtractionQuality] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<KnowledgeDocumentInspectorListItem | null>(null);
  const [header, setHeader] = React.useState<Record<string, any> | null>(null);
  const [profile, setProfile] = React.useState<Record<string, any> | null>(null);
  const [chunks, setChunks] = React.useState<KnowledgePaginatedResponse<KnowledgeDocumentInspectorChunk> | null>(null);
  const [chunkText, setChunkText] = React.useState<Record<string, any> | null>(null);
  const [tab, setTab] = React.useState(0);
  const [chunkType, setChunkType] = React.useState('documentText');
  const [textType, setTextType] = React.useState('');
  const [section, setSection] = React.useState('');
  const [pageNumber, setPageNumber] = React.useState('');
  const documentItems: KnowledgeDocumentInspectorListItem[] = Array.isArray(documents.items) ? documents.items : [];
  const chunkItems: KnowledgeDocumentInspectorChunk[] = chunks && Array.isArray(chunks.items) ? chunks.items : [];

  const loadDocuments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listKnowledgeDocumentInspectorDocuments({
        page,
        limit: 20,
        query: query || undefined,
        sourceKind: sourceKind || undefined,
        extractionStatus: extractionStatus || undefined,
        extractionQuality: extractionQuality || undefined,
      });
      setDocuments(result);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [page, query, sourceKind, extractionStatus, extractionQuality]);

  React.useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  React.useEffect(() => {
    const entityId = window.localStorage.getItem('knowledgeHubInspectorEntityId');
    if (!entityId) return;
    window.localStorage.removeItem('knowledgeHubInspectorEntityId');
    getKnowledgeDocumentInspectorHeader(entityId)
      .then((result) => {
        setSelected({
          id: entityId,
          title: String(result.title || 'Dokument'),
          entityType: String(result.entityType || 'document'),
          sourceKind: result.metadata?.sourceKind,
          sourceIdentity: result.metadata?.sourceIdentity,
          documentType: result.metadata?.documentType,
          catalogType: result.metadata?.catalogType,
          extractionMethod: result.metadata?.extractionMethod,
          extractionQuality: result.metadata?.extractionQuality,
          extractionStatus: result.metadata?.extractionStatus,
          warnings: result.metadata?.extractionWarnings || [],
          pageCount: result.metadata?.pageCount,
          pageMapping: result.metadata?.pageMapping,
          chunkCount: Number(result.chunkCount || 0),
          vectorCount: Number(result.vectorCount || 0),
          sourceUrl: result.metadata?.sourceUrl,
        });
      })
      .catch((requestError) => setError(getErrorMessage(requestError)));
  }, []);

  const loadSelected = React.useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page: 1,
        limit: 100,
        chunkType: tab === 0 ? (chunkType || undefined) : undefined,
        valuableOnly: tab === 1 ? true : undefined,
        textType: textType || undefined,
        section: section || undefined,
        pageNumber: pageNumber || undefined,
      };
      const [headerResult, chunkResult, profileResult] = await Promise.all([
        getKnowledgeDocumentInspectorHeader(selected.id),
        listKnowledgeDocumentInspectorChunks(selected.id, params),
        getKnowledgeDocumentInspectorProfile(selected.id).catch(() => null),
      ]);
      setHeader(headerResult);
      setChunks(chunkResult);
      setProfile(profileResult);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [selected, tab, chunkType, textType, section, pageNumber]);

  React.useEffect(() => {
    void loadSelected();
  }, [loadSelected]);

  const openChunk = async (chunkId: string) => {
    if (!selected) return;
    try {
      setChunkText(await getKnowledgeDocumentInspectorChunkText(selected.id, chunkId));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6">Document Text Inspector</Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only kontroll av normaliserad extraktion, profiler, kvalitet och vectorstatus. Texten är inte en AI-omskrivning.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Sök dokument"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setPage(1); }}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Källtyp"
            value={sourceKind}
            onChange={(event) => { setSourceKind(event.target.value); setPage(1); }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Alla</MenuItem>
            <MenuItem value="asset">DAM asset</MenuItem>
            <MenuItem value="inhouse">Inhouse</MenuItem>
            <MenuItem value="listningar">Listningar</MenuItem>
            <MenuItem value="brandGuide">Brand Guide</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={extractionStatus}
            onChange={(event) => { setExtractionStatus(event.target.value); setPage(1); }}
            sx={{ minWidth: 165 }}
          >
            <MenuItem value="">Alla</MenuItem>
            <MenuItem value="indexed">Indexerad</MenuItem>
            <MenuItem value="ocr-needed">Behöver OCR</MenuItem>
            <MenuItem value="empty">Tom text</MenuItem>
            <MenuItem value="failed">Misslyckad</MenuItem>
            <MenuItem value="stale-source">Stale källa</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Kvalitet"
            value={extractionQuality}
            onChange={(event) => { setExtractionQuality(event.target.value); setPage(1); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Alla</MenuItem>
            <MenuItem value="high">Hög</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Låg</MenuItem>
            <MenuItem value="failed">Misslyckad</MenuItem>
          </TextField>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={24} /></Box>}
        <List dense disablePadding>
          {documentItems.map((document) => {
            const warnings = Array.isArray(document.warnings) ? document.warnings : [];
            return (
            <ListItemButton key={document.id} onClick={() => { setSelected(document); setTab(0); setChunkText(null); }}>
              <ListItemText
                primary={document.title}
                secondary={`${document.sourceKind || 'okänd källa'} · ${document.documentType || document.entityType} · ${document.extractionStatus || 'okänd status'} · ${document.chunkCount} chunks · ${document.vectorCount} vectors`}
              />
              <Stack direction="row" spacing={0.5}>
                <Chip size="small" label={document.extractionQuality || 'okänd kvalitet'} />
                {document.extractionStatus === 'ocr-needed' && <Chip size="small" color="warning" label="OCR behövs" />}
                {document.pageMapping === 'available' && <Chip size="small" color="success" label="Sidmappad" />}
                {warnings.length > 0 && <Chip size="small" color="warning" label={`${warnings.length} varningar`} />}
              </Stack>
            </ListItemButton>
            );
          })}
        </List>
        {documents.pages > 1 && (
          <Pagination page={page} count={documents.pages} onChange={(_event, value) => setPage(value)} />
        )}
      </Stack>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          {selected?.title}
          <IconButton onClick={() => setSelected(null)} sx={{ position: 'absolute', right: 12, top: 10 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {header && (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={String(header.metadata?.sourceKind || '')} />
                <Chip size="small" label={String(header.metadata?.documentType || '')} />
                <Chip size="small" label={`Extraktion: ${String(header.metadata?.extractionMethod || 'okänd')}`} />
                <Chip size="small" label={`Kvalitet: ${String(header.metadata?.extractionQuality || 'okänd')}`} />
                <Chip size="small" color={header.metadata?.extractionStatus === 'ocr-needed' ? 'warning' : 'default'} label={`Status: ${String(header.metadata?.extractionStatus || 'okänd')}`} />
                <Chip size="small" color={header.metadata?.pageMapping === 'available' ? 'success' : 'default'} label={`Sidor: ${String(header.metadata?.pageMapping || 'okänd')}`} />
                <Chip size="small" label={`${header.chunkCount || 0} chunks`} />
                <Chip size="small" label={`${header.vectorCount || 0} vectors`} />
                {safeSourceUrl(header.metadata?.sourceUrl) && (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(safeSourceUrl(header.metadata?.sourceUrl)!, '_blank', 'noopener,noreferrer')}
                  >
                    Öppna källa
                  </Button>
                )}
              </Stack>
            )}
            <Tabs value={tab} onChange={(_event, value) => { setTab(value); setChunkText(null); }}>
              <Tab label="Extraherad text" />
              <Tab label="Värdefull text" />
              <Tab label="Profil" />
              <Tab label="Kvalitet/metadata" />
            </Tabs>
            {(tab === 0 || tab === 1) && (
              <>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField size="small" label="Chunktyp" value={chunkType} onChange={(event) => setChunkType(event.target.value)} />
                  <TextField size="small" label="Texttyp" value={textType} onChange={(event) => setTextType(event.target.value)} />
                  <TextField size="small" label="Avsnitt" value={section} onChange={(event) => setSection(event.target.value)} />
                  <TextField size="small" label="Sida" value={pageNumber} onChange={(event) => setPageNumber(event.target.value)} />
                </Stack>
                {header?.metadata?.pageMapping === 'unavailable' && (
                  <Alert severity="info">Exakta sidnummer visas inte eftersom riktig PDF-sidmapping saknas.</Alert>
                )}
                <List dense>
                  {chunkItems.map((chunk) => (
                    <ListItemButton key={chunk.id} selected={chunkText?.id === chunk.id} onClick={() => openChunk(chunk.id)}>
                      <ListItemText
                        primary={`${chunk.chunkType}${chunk.metadata?.textType ? ` · ${chunk.metadata.textType}` : ''}`}
                        secondary={[
                          chunk.metadata?.sectionTitle,
                          chunk.metadata?.pageNumber ? `Sida ${chunk.metadata.pageNumber}` : '',
                          chunk.vector?.vectorStore === 'qdrant' ? 'Vector klar' : 'Ingen vector',
                        ].filter(Boolean).join(' · ')}
                      />
                    </ListItemButton>
                  ))}
                </List>
                {chunkText && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfcfe' }}>
                    <Typography variant="caption" color="text.secondary">Normaliserad extraherad text</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1, lineHeight: 1.6 }}>{chunkText.text}</Typography>
                  </Paper>
                )}
              </>
            )}
            {tab === 2 && (
              profile ? (
                <Stack spacing={1}>
                  <Chip size="small" label={String(profile.status || '')} sx={{ alignSelf: 'flex-start' }} />
                  <Typography variant="subtitle2">{profile.summaryShort}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{profile.summaryDetailed}</Typography>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">Sökord</Typography>
                  <Typography variant="body2">{(profile.search?.keywords || []).join(', ') || '–'}</Typography>
                </Stack>
              ) : <Alert severity="info">Ingen dokumentprofil finns.</Alert>
            )}
            {tab === 3 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', m: 0 }}>
                  {JSON.stringify(header?.metadata || {}, null, 2)}
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default DocumentTextInspector;
